'use client'

import { Field, Heading, Input, Button } from '@/components/ui'

import { useState } from 'react'
import Link from 'next/link'
import { signIn } from 'next-auth/react'

export default function SignUpPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          inviteCode: formData.inviteCode || undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed')
      }

      setSuccess(
        data.message ||
          'Account created. Check your email for a verification link before signing in.'
      )
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        inviteCode: '',
      })
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = () => {
    signIn('google', { callbackUrl: '/' })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <Heading level={2} className="mt-6 text-center text-ink-strong">
            Create your account
          </Heading>
          <p className="mt-2 text-center text-sm text-muted">
            Or{' '}
            <Link href="/auth/signin" className="font-medium text-accent-foreground hover:text-accent-foreground">
              sign in to your existing account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div role="alert" className="rounded-md bg-danger-subtle p-4">
              <div className="text-sm text-danger">{error}</div>
            </div>
          )}
          {success && (
            <div className="rounded-md bg-success-subtle p-4">
              <div className="text-sm text-success">{success}</div>
            </div>
          )}
          <div className="space-y-4">
            <Field label="Full name" htmlFor="name">
              <Input
                id="name"
                name="name"
                type="text"
                required
                className="block w-full"
                placeholder="Full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Field>
            <Field label="Email address" htmlFor="email-address">
              <Input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full"
                placeholder="Email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="block w-full"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </Field>
            <Field label="Confirm Password" htmlFor="confirm-password">
              <Input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="block w-full"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
            </Field>
            <Field label="Invite code" htmlFor="invite-code">
              <Input
                id="invite-code"
                name="invite-code"
                type="text"
                autoComplete="off"
                className="block w-full uppercase tracking-wider"
                placeholder="Invite code"
                value={formData.inviteCode}
                onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>

          <div>
            <Button variant="primary" loading={isLoading}
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center disabled:opacity-50"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-line-strong" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-canvas text-muted">Or continue with</span>
            </div>
          </div>

          <div>
            <Button variant="secondary"
              type="button"
              onClick={handleGoogleSignIn}
              className="group relative w-full flex justify-center"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign up with Google
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}